import _, { cloneDeep, round, sumBy, toNumber } from "lodash";
import React, { Fragment, useEffect, useMemo, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { Link, useLocation, useParams } from "react-router-dom";
import {
  CircularProgress,
  Icon,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Badge,
  Switch,
  Box
} from "@mui/material";
import { amber } from "@mui/material/colors";
import { AuraSimpleTableHead } from "app/main/apps/common/AuraSimpleTable";
import { calculateUnitPrice, getDiscountAmtFromPercentage, calculateOrderItemTotalAmount, formatter, displayName, formatWeight, isValidObject, checkNullOrUndefined, isVasTeam, preventNegativeValues, resolveFobDisplay } from "app/main/apps/common/AuraFunctions";
import { calculateSurchargeExact, addMoneyExact } from 'app/main/apps/common/money';
import FuseSvgIcon from "@fuse/core/FuseSvgIcon/FuseSvgIcon";
import { useDispatch, useSelector } from "react-redux";
import DeleteConfirmation from "../Dialogs/DeleteConfirmation";
import ApprovalContent from "../Dialogs/ApprovalContent";
import { productInitValues } from "../../product/ProductBase";
import { openProductDialog, openViewProductDialog } from "../../store/productSlice";
import VasPriceList from '../Vas/VasPriceList';
import { showVasPriceDetails } from "../Vas/VasHelper";
import { findShortStockLines, shortStockMessage } from "../mixedOrderRestriction";
import fieldRowKey from "app/main/apps/common/fieldRowKey";
import { productNameStyle, wrapsProductName } from './productNameCell';
import { formatOrderLineFob } from './orderLineFob';

function OrderItems({
  fields = [],
  control,
  hasManageProductPermission,
  isReadOnly,
  isview,
  remove,
  setDetails,
  setOpenDialog,
  getVASPricing,
  orderDetails,
  type,
  handleCustomzation,
  directShipment,
  // PGSS-4978/4979: supplier-order Create Supplier Order only — gray in-stock lines and narrow summary
  // totals to non-stock lines on a mixed cart. Default false; ProductsTab and other callers unchanged.
  grayInStockLines = false,
  // PGSS-5158: ON unless the settings row turns it off. Passed down rather than fetched again so
  // the note and the Save button it explains can never disagree.
  mixedOrderGuardEnabled = false,
  priceEditable = false,
  qtyEditable = directShipment,
  hideQty,
  orderType,
  param,
  pageType,
  hideAdditionalFields,
  isCustomer,
  saveOrderItemLocally,
  hideActions = false,
  hideTotalSection = false,
  hideVASPriceDetails = false,
  showReceivedBoxNumber = false,
  formOrderData = null,
  hiddenFields = {
    squareFeet: hideAdditionalFields
  },
  reloadData,
  shouldDisplaySurcharge,
  hasValidSurcharge,

}) {

  const { orderId } = useParams();
  const [loading, setLoading] = useState(false);
  const [approvalDialog, setApprovalDialog] = useState({});
  const [deleteConfirmationDialog, setDeleteConfirmationDialog] = useState({
    open: false
  })
  const orderData = useSelector(({ eCommerceApp }) => {
    return eCommerceApp.order ? eCommerceApp.order.data : null;
  });
  const {
    formState: { errors },
    watch, getValues, setValue
  } = useFormContext();

  const formData = getValues()

  const watchProducts = watch("products");
  // Re-render totals when surcharge or its resolved default changes (PGSS-4936 edit preview).
  watch("sur_charge_percentage");
  watch("default_sur_charge_percentage");
  const isQuoteEdit = orderType === 'quote' && pageType === 'edit'
  const isQuote = orderType === 'quote' || param === 'convert-to-quote'
  const [totalVASAmount, setTotalVASAmount] = useState(0);

  useEffect(() => {
    setValue('weight', Number(getValues()?.weight))
  }, [setValue])

  useEffect(() => {
    if(reloadData === true) {
      // After clicking 'Delete VAS', totalVASAmount retains its prior value, causing incorrect Total Amount to be displayed
      // To address this, the reloadData field is used to reset totalVASAmount to 0 post-deletion
      // reloadData is set to true within the order component when the 'Delete VAS' button is triggered
      setTotalVASAmount(0)
    }
  }, [reloadData])

  const handleDeleteDialog = (show, currentIndex) => {
    setDeleteConfirmationDialog((prev) => ({ ...prev, open: show, index: currentIndex }))
  }

  const handleDeleteSubmit = () => {
    remove(deleteConfirmationDialog.index)
    setDeleteConfirmationDialog({ open: false })
  }

  const hideVas = [
    "Drawing Revision Requested",
    "Received",
    "Drawing Rejected",
  ].includes(orderData?.status?.name);

  const dispatch = useDispatch();
  const location = useLocation();
  const { pathname, search } = location;
  const queryString = search.startsWith('?') ? search.slice(1) : search;
  // Split the query string into key-value pairs
  const queryParams = new URLSearchParams(queryString);
  // Get the value of the "type" parameter
  const typeValue = queryParams.get("type");
  const hideFields = true
  const showDrawingApproval = (data) => {
    return (orderData.is_drawing_approval || data?.is_custom || data?.product?.is_custom || isCustomer);
  }

  /**
   * PGSS-5158: standard lines this order cannot fill from stock, keyed by row.
   *
   * Derived from `orderDetails` -- the watched array, so the note follows what is typed rather than
   * what was last saved. `fields` is the useFieldArray snapshot and lags a keystroke behind, which
   * is why the quantity read here comes from the same array the totals row uses.
   *
   * Staff only, mirroring the Save button: the message names how many units are on hand.
   */
  const shortStockByIndex = useMemo(() => {
    const map = new Map();
    if (!mixedOrderGuardEnabled || isCustomer) return map;
    const live = orderDetails || [];
    findShortStockLines(live).forEach((entry) => {
      const idx = live.indexOf(entry.line);
      if (idx >= 0) map.set(idx, entry);
    });
    return map;
  }, [orderDetails, mixedOrderGuardEnabled, isCustomer]);

  const calculatePrice = (fob, qty, orderDetail) => {
    const { weight, margin, tariff, shipping } = orderDetail.product || {};
    return calculateUnitPrice(weight, fob, tariff, shipping, margin);
  }

  // the below method calculates the sur charge amount from the entered percentage
  const calculateSurchargeAmount = () => {
    // PGSS-4936: mirror the backend SurchargeResolver so the previewed amount equals the saved amount —
    // an explicit value (including 0) wins; blank/empty applies the resolved default surcharge.
    const raw = getValues("sur_charge_percentage");
    const surchargePercentage =
      raw !== null && raw !== undefined && raw !== ""
        ? Number(raw) || 0
        : Number(getValues("default_sur_charge_percentage")) || 0;
    if (!surchargePercentage) return 0; // explicit 0 / no default → not calculated

    // PGSS-5025: sum the line totals in exact decimal. Accumulating them as floats re-introduced the very
    // binary error we are removing (the running `sum + Number(...)` drifts before the surcharge is applied).
    const subtotal = addMoneyExact(
      ...(orderDetails || []).map((o) =>
        calculateOrderItemTotalAmount(o?.unit_price, o?.quantity, o?.discount)
      )
    );

    // Calculate surcharge amount - this excludes vas amount in the calc.
    // PGSS-5025: exact decimal HALF_UP, identical to the backend's
    // OrderCalculationService.calculateSurchargeAmount (base × pct ÷ 100, HALF_UP to 2dp).
    // The previous `Math.round(subtotal × pct) / 100` was the right *formula* but still ran on floats,
    // so it kept drifting a cent at exact half-cent boundaries.
    return Number(calculateSurchargeExact(subtotal, surchargePercentage));
  };

  const orderCols = (orderDetail, index) => {
    const orderDetailForm = formData?.order_details[index]
    return (
      <>
        <td className="w-100 px-8 py-4">
          <span className="truncate">{orderDetail?.product?.sub_type?.inputValue ? orderDetail?.product?.sub_type?.inputValue : orderDetail?.product?.sub_type?.name}</span>
        </td>

        <td className="w-106 px-8 py-4">
          <Controller
            control={control}
            name={`order_details.${index}.customer_provider_number`}
            render={({ field }) => (
              <>
                {!hideFields && (
                  <TextField
                    {...field}
                    variant="standard"
                    fullWidth
                    className="p-0"
                    size="small"
                    InputProps={{
                      classes: {
                        input: "FuseSearch-input py-4 h-20 w-92",
                        notchedOutline: "rounded-8",
                      },
                      readOnly: !hasManageProductPermission || isReadOnly,
                    }}
                  />
                )}
                {hideFields && (
                  <Tooltip title={field.value}>
                    <Typography className="h-20 w-92 truncate">{field.value}</Typography>
                  </Tooltip>
                )}
              </>
            )}
          />
        </td>
        <td className="w-106 px-8 py-4">
          <Controller
            control={control}
            name={`order_details.${index}.decedent`}
            render={({ field }) => (
              <>
                {!hideFields && (
                  <TextField
                    {...field}
                    variant="standard"
                    fullWidth
                    className="p-0"
                    size="small"
                    InputProps={{
                      classes: {
                        input: "FuseSearch-input py-4 h-20 w-96",
                        notchedOutline: "rounded-8",
                      },
                      readOnly: !hasManageProductPermission || isReadOnly,
                    }}
                  />
                )}
                {hideFields && (
                  <Tooltip title={field.value}>
                    <Typography className="h-20 w-64 truncate">{field.value}</Typography>
                  </Tooltip>
                )}
              </>
            )}
          />
        </td>
        <td className="w-100 px-8 py-4">
          <span className="truncate">
            <Controller
              control={control}
              name={`order_details.${index}.quantity`}
              render={({ field }) => (
                <>
                  {!hideFields && (
                    <TextField
                      {...field}
                      onChange={(ev) => {
                        field.onChange(Number(ev.target.value));
                        setValue(`order_details.${index}.unit_price`, calculatePrice(getValues().order_details[index].unit_cost, ev.target.value, orderDetail))
                      }}
                      value={Number(field.value) || ""}
                      type="number"
                      variant="standard"
                      fullWidth
                      className="p-0"
                      size="small"
                      InputProps={{
                        classes: {
                          input: "FuseSearch-input py-4 h-20 w-64",
                          notchedOutline: "rounded-8",
                        },
                        readOnly: !hasManageProductPermission || isReadOnly,
                      }}
                    />
                  )}
                  {hideFields && (
                    <Typography className="h-20 w-62">{field.value}</Typography>
                  )}
                  {/* PGSS-5158: an error, unlike the type-rule note -- this one stops the save,
                      and the remedy (a smaller quantity) is on this very field. */}
                  {!hideFields && shortStockByIndex.has(index) && (
                    <Typography className="text-11 text-red-700 mt-4 whitespace-normal w-96">
                      {shortStockMessage(shortStockByIndex.get(index))}
                    </Typography>
                  )}
                </>
              )}
            />
          </span>
        </td>
        {
          isQuoteEdit || param === 'convert-to-quote' ? (
            <td className="w-100 px-8 py-4 text-center">
              <span className="truncate">
                <Controller
                  control={control}
                  name={`order_details.${index}.weight`}
                  rules={{
                    required: {
                      value: true,
                      message: 'Required'
                    }
                  }}
                  render={({ field }) => (
                    <>
                      {!hideFields && (
                        <TextField
                          {...field}
                          onChange={(ev) => {
                            field.onChange(Number(ev.target.value));
                            if (isQuoteEdit) {
                              setValue(`order_details.${index}.unit_price`, calculatePrice(orderDetails))
                            }
                          }}
                          value={Number(field.value) || ""}
                          type="number"
                          variant="standard"
                          fullWidth
                          className="p-0"
                          size="small"
                          error={!!errors?.order_details?.[index]?.weight}
                          helperText={errors?.order_details?.[index]?.weight?.message}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="start">lbs</InputAdornment>
                            ),
                            classes: {
                              input: "FuseSearch-input py-4 h-20 w-64",
                              notchedOutline: "rounded-8",
                            },
                            readOnly: !hasManageProductPermission || isReadOnly,
                          }}
                        />
                      )}
                      {hideFields && (
                        <Typography className="h-20">{field.value} lbs</Typography>
                      )}
                    </>
                  )}
                />
              </span>
            </td>
          ) : (
            <td className="w-100 px-8 py-4">
              <span className="truncate">
                {orderDetail?.product?.weight
                  ? `${formatWeight(orderDetail?.product?.weight)}`
                  : ""}
              </span>
            </td>
          )
        }
        {!hiddenFields?.squareFeet && <td className="w-100 px-8 py-4">
          <span className="truncate">
            <Typography className="h-20 w-62">{orderDetail?.product?.square_feet}</Typography>
          </span>
        </td>}
        {!hideAdditionalFields && <td className="w-100 px-8 py-4">
          <span className="truncate">
            <Controller
              control={control}
              name={`order_details.${index}.unit_cost`}
              render={({ field }) => (
                <>
                  {!hideFields && (
                    <TextField
                      {...field}
                      onChange={(ev) => {
                        field.onChange(Number(ev.target.value));
                        setValue(`order_details.${index}.unit_price`, calculatePrice(ev.target.value, getValues().order_details[index].quantity, orderDetail))
                      }}
                      type="number"
                      variant="standard"
                      fullWidth
                      className="p-0"
                      size="small"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">$</InputAdornment>
                        ),
                        classes: {
                          input: "FuseSearch-input py-4 h-20",
                          notchedOutline: "rounded-8",
                        },
                        readOnly: !hasManageProductPermission || isReadOnly,
                      }}
                    />
                  )}
                  {hideFields && (
                    // PGSS-5673: same FOB the Edit / View Product dialog shows (a cleared 0 shows as $0.00).
                    <Typography className="h-20 w-62">{formatOrderLineFob(orderDetail, field.value)}</Typography>
                  )}
                </>
              )}
            />
          </span>
        </td>}
        {!hideAdditionalFields && <td className="w-100 px-8 py-4">
          <span className="truncate">
            <Controller
              control={control}
              name={`order_details.${index}.unit_price`}
              render={({ field }) => (
                <>
                  {(!hideFields && !orderDetail?.product?.is_custom) && (
                    <TextField
                      {...field}
                      onChange={(ev) => {
                        field.onChange(Number(ev.target.value));
                      }}
                      value={field.value || ""}
                      type="number"
                      variant="standard"
                      fullWidth
                      className="p-0"
                      size="small"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">$</InputAdornment>
                        ),
                        classes: {
                          input: "FuseSearch-input py-4 h-20",
                          notchedOutline: "rounded-8",
                        },
                        readOnly: !hasManageProductPermission || isReadOnly,
                      }}
                    />
                  )}
                  {(hideFields || orderDetail?.product?.is_custom) && (
                    <Typography className="">{field.value && formatter.format(field.value)}</Typography>
                  )}
                </>
              )}
            />
          </span>
        </td>}
        {!isCustomer && !hideAdditionalFields && <td className="w-100 px-8 py-4">
          <span className="truncate">
            {orderDetail?.unit_price && formatter.format(getDiscountAmtFromPercentage(orderDetail?.discount, orderDetail?.unit_price, orderDetail?.quantity))}
          </span>
        </td>}
        <td className="w-100 px-8 py-4">
          <span className="truncate">
            {formatWeight(Number(orderDetail?.product?.weight) *
              Number(orderDetails?.[index]?.quantity))}
          </span>
        </td>
        {
          !hideAdditionalFields && (
            <td className="w-100 px-8 py-4">
              <span className="truncate">
                {formatter.format(calculateOrderItemTotalAmount(orderDetail?.unit_price, orderDetails?.[index]?.quantity, orderDetail?.discount))}
              </span>
            </td>
          )
        }
        {showReceivedBoxNumber === true && (
          <td className="max-w-60 md:max-w-240 px-8 py-4">
            <Typography>
              {orderDetail.received_box_number &&
                orderDetail.received_box_number.replace(/,/g, ", ")}
            </Typography>
          </td>
        )}
        { !hideActions && (
        <td>
            {orderDetail?.product?.is_custom && (
              <Controller
                name={`order_details.${index}.is_has_drawing`}
                type="checkbox"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <>
                    <Switch
                      checked={value}
                      className={`${isview ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                      onChange={(ev) => {
                        const {checked} = ev.target;
                        onChange(checked);
                        if(pageType !== 'new' && pageType !== 'view') {
                          // Update is_data_editted. send is_data_editted field to notify this order_details is updated
                          setValue(`order_details.${index}.is_data_editted`, true);
                        }
                      }}
                      disabled={isview}
                    />
                  </>
                )}
              />
            )}
        </td>)}
        {!hideActions && <td className="text-center">
          {hasManageProductPermission && (
            <div className="flex">
              {!isview && (
                <IconButton
                  className="cl-icon "
                  size="small"
                  onClick={() => {
                    handleDeleteDialog(true, index)
                    // remove(index);
                  }}
                >
                  <Icon>clear</Icon>
                </IconButton>
              )}
              {(isview ? orderDetail.drawing_documents : showDrawingApproval(orderDetail)) && (
                <Tooltip title="Attach or View Diagram">
                  <Badge color="error" className="action-badge" overlap="circular" badgeContent={<Icon sx={{ fontSize: '12px', rotate: '270deg' }} fontSize="small">attachment</Icon>} anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                    invisible={!(orderDetailForm?.drawing_documents?.length > 0 || orderDetailForm?.new_drawing_documents?.length > 0)}
                  >
                    <FuseSvgIcon
                      className="custom-icon"
                      size={24}
                      onClick={() => {
                        setApprovalDialog({
                          type: "drawingApproval",
                          title: "Attach Diagram",
                          open: true,
                          attachmentProps: {
                            payload: { image_customisation_type: "drawing" },
                            attachmentsType: `order_details.${index}.drawing_documents`,
                            newAttachmentsType: `order_details.${index}.new_drawing_documents`,
                            onAttachmentChange: () => {
                              if(pageType !== 'new' && pageType !== 'view') {
                                // Update is_data_editted. send is_data_editted field to notify this order_details is updated
                                setValue(`order_details.${index}.is_data_editted`, true);
                              }
                            }
                          },
                          isview,
                        });
                      }}
                    >
                      material-solid:attach_file
                    </FuseSvgIcon>
                  </Badge>
                </Tooltip>
              )}
              {(isview && orderDetail.proof_documents) && (
                <Tooltip title="Proof">
                  <Badge color="error" className="action-badge" overlap="circular" badgeContent={<Icon sx={{ fontSize: '12px', rotate: '270deg' }} fontSize="small">attachment</Icon>} anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                    invisible={!(orderDetailForm?.proof_documents?.length > 0 || orderDetailForm?.new_proof_documents?.length > 0)}
                  >
                    <FuseSvgIcon
                      className="custom-icon"
                      size={30}
                      onClick={() => {
                        setApprovalDialog({
                          type: "proofApproval",
                          title: "Proof Documents",
                          open: true,
                          attachmentProps: {
                            payload: { image_customisation_type: "proof" },
                            attachmentsType: `order_details.${index}.proof_documents`,
                            newAttachmentsType: `order_details.${index}.new_proof_documents`,
                          },
                          isview
                        });
                      }}
                    >
                      material-solid:attach_file
                    </FuseSvgIcon>
                  </Badge>
                </Tooltip>
              )}

              {!isview && (
                <Tooltip
                  title="Edit Product"
                >
                  <FuseSvgIcon
                    className="custom-icon"
                    size={30}
                    onClick={() => {
                      if (orderDetail?.product_id) {
                        dispatch(
                          openProductDialog({
                            data: productInitValues({ ...orderDetail.product, discount: orderDetail.discount, order_quantity: orderDetail.quantity, customer_provider_number: orderDetail.customer_provider_number, comments: orderDetail.comments, decedent: orderDetail.decedent }),
                            param: {
                              name: "edit_custom_product",
                              title: orderDetail?.product?.is_custom ? "Edit Custom Product" : "Edit Inventory Product",
                              orderType: param,
                              stage: orderType,
                              isReadOnly: false,
                              hideDialogActions: false,
                              isCustomProduct: orderDetail?.product?.is_custom,
                              isInventoryProduct: !orderDetail?.product?.is_custom,
                              hideAdditionalFields,
                              saveProductLocally: saveOrderItemLocally,
                              formOrderType:formOrderData?.order_type,
                              formOrderData: cloneDeep(formOrderData)
                            },
                          })
                        );
                       }
                    }}
                  >
                    material-solid:create
                  </FuseSvgIcon>
                </Tooltip>
              )}
              {orderDetail?.product?.is_custom && !isview && (
                <Tooltip
                  title="Copy Product"
                >
                  <FuseSvgIcon
                    className="custom-icon"
                    size={30}
                    onClick={() => {
                      if (orderDetail?.product_id) {
                        dispatch(
                          openProductDialog({
                            data: productInitValues({ ...orderDetail.product, discount: orderDetail.discount, order_quantity: orderDetail.quantity, customer_provider_number: orderDetail.customer_provider_number, comments: orderDetail.comments, decedent: orderDetail.decedent, weight: orderDetail.weight  }),
                            param: {
                              name: "custom_product",
                              title: "Add Custom Product",
                              orderType: param,
                              stage: orderType,
                              isReadOnly: false,
                              hideDialogActions: false,
                              cloneProduct: true,
                              isCustomProduct: orderDetail?.product?.is_custom,
                              hideAdditionalFields,
                              formOrderType:formOrderData?.order_type,
                              formOrderData: cloneDeep(formOrderData)
                            },
                          })
                        )
                      }
                    }}
                  >
                    material-solid:content_copy
                  </FuseSvgIcon>
                </Tooltip>
              )}
            </div>
          )}
        </td>
        }
      </>
    )
  };

  const reOrderCols = (orderDetail, index) => (
    <>
      {!hideQty && <td className="w-64 px-8 py-4">
        <span className="truncate">
          <Controller
            control={control}
            name={`products.${index}.quantity`}
            render={({ field }) => (
              <TextField
                {...field}
                onChange={(ev) => {
                  if (ev.target.value !== "e")
                    field.onChange(Number(ev.target.value));
                }}
                onKeyDown={preventNegativeValues}
                type="number"
                variant="standard"
                fullWidth
                className="p-0"
                size="small"
                disabled={directShipment && !qtyEditable}
                InputProps={{
                  classes: {
                    notchedOutline: "rounded-8",
                  },
                  inputProps: { min: 0 },
                }}
                error={!!errors?.products?.[index]?.quantity}
                helperText={errors?.products?.[index]?.quantity?.message}
              />
            )}
          />
        </span>
      </td>}
      <td className="w-72 px-8 py-4">
        {
          !hideQty ? (
            <span className="truncate">
              {directShipment || priceEditable ? (
                <Controller
                  control={control}
                  name={`products.${index}.unit_cost`}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      onChange={(ev) => {
                        if (ev.target.value !== "e")
                          field.onChange(ev.target.value ? Number(ev.target.value) : '');
                      }}
                     onKeyDown={preventNegativeValues}
                      type="number"
                      variant="standard"
                      fullWidth
                      className="p-0"
                      size="small"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">$</InputAdornment>
                        ),
                        classes: {
                          input: "FuseSearch-input py-4 h-20 w-64",
                          notchedOutline: "rounded-8",
                        },
                      }}
                      required
                      error={!!errors?.products?.[index]?.unit_cost}
                      helperText={errors?.products?.[index]?.unit_cost?.message}
                    />
                  )}
                />
              ) : (
                // PGSS-5123: the FOB column must show the effective FOB cost (fob when > 0, else
                // unit_cost — the backend's rule), not the raw unit_cost. An inventory product with
                // fob=120 / unit_cost=100 was rendering 100 in the FOB column.
                // PGSS-5377: ...but a FOB the user explicitly cleared shows as cleared rather than
                // resolving back to the untouched unit_cost. Display only — the line's cost basis
                // (seeded in Order.js / ProductsTab.js) still uses resolveFobCost.
                formatter.format(resolveFobDisplay(orderDetail.product))
              )}
            </span>
          ) : '-'
        }
      </td>
    </>
  );

  const orderPricingNewCols = (
    <TableBody>
      <TableRow>
        <TableCell>
          <Typography className="font-normal text-13 ">
            Total Item(s)
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Typography className="font-normal">
            {sumBy(orderDetails, function (o) {
              return toNumber(o?.quantity);
            })}
          </Typography>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell>
          <Typography className="font-normal text-13 ">Total Weight</Typography>
        </TableCell>
        <TableCell align="right">
          <Typography className="font-normal">
            {sumBy(orderDetails, function (o) {
              return toNumber(o?.quantity) * toNumber(o?.product?.weight);
            })}{" "}
            lbs
          </Typography>
        </TableCell>
      </TableRow>

      {/* This portion is visible only in case if the setting for surcharge is enabled */}
      {shouldDisplaySurcharge && (
        <TableRow>
          <TableCell>
            <Typography className="font-normal text-13 ">
              Surcharge %
            </Typography>
          </TableCell>
          <TableCell align="right">
          <Controller
            name="sur_charge_percentage"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                className="w-[100px] ml-auto"
                id="sur_charge_percentage"
                size="small"
                type="number"
                variant="standard"
                onKeyDown={preventNegativeValues}
                InputProps={{
                  readOnly: isReadOnly,
                  sx: {
                    paddingRight: 0, // Remove right padding
                    "&::before": {
                      borderBottom: "2px solid #1565c0",
                      left: "auto", // Reset left positioning
                      right: 0, // Align to right
                      width: "calc(100% - 12px)", // Shrink line width
                    },
                    "&::after": {
                      borderBottom: "2px solid #0d47a1",
                      left: "auto", // Reset left positioning
                      right: 0, // Align to right
                      width: "calc(100% - 12px)", // Shrink line width
                    },
                    "& .MuiInput-input": {
                      textAlign: "right",
                      paddingRight: "4px", // Minimal right padding
                    },
                  },
                }}
                sx={{
                  "& .MuiInput-root": {
                    "&:hover:not(.Mui-disabled):before": {
                      borderBottom: "2px solid #1976d2",
                      left: "auto",
                      right: 0,
                      width: "calc(100% - 12px)",
                    },
                  },
                }}
                error={!!errors.sur_charge_percentage}
                // PGSS-4936: tell users blank applies the default surcharge, 0 = none (matches the
                // backend SurchargeResolver: blank/null → default, 0 → no surcharge).
                helperText={errors?.sur_charge_percentage?.message
                  || (isReadOnly ? undefined : "Leave blank to apply the default surcharge; enter 0 for no surcharge.")}
              />
            )}
          />
          </TableCell>
          
        </TableRow>
      )}

      {/* This block displays value if the setting is enabled or disabled or if it has existing value  */}
      {(shouldDisplaySurcharge || hasValidSurcharge) && (
        <TableRow>
          <TableCell>
            <Typography className="font-normal text-13 ">
              Additional Surcharge Amount
            </Typography>
          </TableCell>
          <TableCell align="right">
            <Typography className="font-normal">
              {formatter.format(calculateSurchargeAmount())}
            </Typography>
          </TableCell>
        </TableRow>
      )}
      {!hideAdditionalFields && (
        <TableRow>
          <TableCell>
            <Typography className="font-normal text-13 ">
              Total Amount
            </Typography>
          </TableCell>
          <TableCell align="right">
            <Typography className="font-normal">
              {formatter.format(
                sumBy(orderDetails, function (o) {
                  return Number(
                    calculateOrderItemTotalAmount(
                      o?.unit_price,
                      o?.quantity,
                      o?.discount
                    )
                  );
                }) +
                  (totalVASAmount || 0) +
                  calculateSurchargeAmount()
              )}
            </Typography>
          </TableCell>
        </TableRow>
      )}
    </TableBody>
  );

  // PGSS-4979: for a mixed Create Supplier Order only the non-stock lines are actually sourced from the
  // supplier, so the summary (Total Item(s) / Weight / Price) must exclude the grayed in-stock lines.
  // For every other flow (inventory / direct-shipment / all-stock / order tabs) the totals cover all lines.
  const summaryProducts = grayInStockLines
    ? (watchProducts || []).filter((p) => p?.is_stock_product !== true)
    : watchProducts;
  const reOrderPricingNewCols = (
    <TableBody>
      {!hideQty && (
        <>
          <TableRow>
            <TableCell>
              <Typography className="font-normal text-13 ">Total Item(s)</Typography>
            </TableCell>
            <TableCell align="right">
              <Typography className="font-normal">
                {sumBy(summaryProducts, function (o) {
                  return toNumber(o?.quantity);
                })}
              </Typography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>
              <Typography className="font-normal text-13 ">Total Weight</Typography>
            </TableCell>
            <TableCell align="right">
              <Typography className="font-normal">
                {sumBy(summaryProducts, function (o) {
                  return toNumber(o.quantity) * toNumber(o.weight);
                })}{" "}
                lbs
              </Typography>
            </TableCell>
          </TableRow>
        </>
      )}
      <TableRow>
        <TableCell>
          <Typography className="font-normal text-13 ">Total Price</Typography>
        </TableCell>
        <TableCell align="right">
          <Typography className="font-normal">
            {formatter.format(sumBy(summaryProducts, function (o) {
                return !hideQty ? toNumber(o?.quantity) * toNumber(o?.unit_cost): 0;
              }))}
          </Typography>
        </TableCell>
      </TableRow>
    </TableBody>
  )

  const headers = [
    { name: "No" },
    { name: "Name" },
    { name: "SubType" },
    { name: `${displayName?.weight}` },
    { name: "Quantity", hide: hideQty },
    { name: "Price" },
  ];

  const orderTableHeaders = [
    { name: "No" },
    { name: "Name" },
    { name: "Sub Type" },
    { name: "Reference No" },
    { name: "Decedent" },
    { name: "Qty" },
    { name: `${displayName?.weight}` },
    { name: "Sq. ft", hide: hiddenFields.squareFeet },
    { name: "FOB", hide: hideAdditionalFields },
    { name: "Unit Price", hide: hideAdditionalFields },
    { name: "Discount", hide: isCustomer || hideAdditionalFields },
    { name: `Total ${displayName?.weight}` },
    { name: "Total Amount", hide: hideAdditionalFields },
    {name: "Box Number",hide: !showReceivedBoxNumber },
    { name: "Request product drawing" , hide: hideActions},
    { name: "Action", hide: hideActions },
  ];

  /**
  * Handles Whethether can user just view the Vas Price details or option to add/edit it
  */
  const getVASPriceDetailsViewingType = () => {
    if(isview || (orderType === "rfq" && param !== 'convert-to-quote'))
      return "view"
    return "add"
  }

  return (
    <div className="table-responsive">
        <div className='w-full'>
        {loading && (
          <div className="my-20 flex justify-center items-center">
        <CircularProgress />
          </div>
        )}
      <table className="w-full">
      {loading ? null : (
       <>
        <AuraSimpleTableHead
          headerList={type === "order" ? orderTableHeaders : headers}
          classes={{ cnCell: "font-semibold px-8 py-8 w-300 cell-class align-text-top" }}
        />
        <tbody className="mt-16">
          {fields.map((data, index) => {
            const orderDetail = type === "order" ? data : { product: data };
            // PGSS-4961: read is_stock_product from current form state (watch), not the useFieldArray
            // `fields` snapshot. `fields` doesn't carry this non-registered property (no input binds it)
            // through to this child, so reading it off `data`/`orderDetail` returns undefined and the row
            // never grays even though the API returns is_stock_product correctly. watch('products') is
            // authoritative; fall back to the field data for the order-tab (non-directShipment) shape.
            const lineStockFlag = watchProducts?.[index]?.is_stock_product
              ?? orderDetail?.product?.is_stock_product;
            // PGSS-4978: only gray in-stock lines for a MIXED Create Supplier Order (grayInStockLines).
            // Inventory replenishment, direct-shipment, and all-stock orders legitimately source stock and
            // must stay interactive — otherwise their rows (and the submit) are blocked.
            const isInStockLine = grayInStockLines === true && lineStockFlag === true;
            const inStockNote = 'In stock — ships from inventory, no sourcing needed';
            // Name / comments = inspect only from in-memory line data (no Update, no master API fetch).
            // Real edits use the pencil when !isview (seeds the same line fields with saveProductLocally).
            const openProductInspectDialog = () => {
              if (!orderDetail?.product) return;
              const isCustom = orderDetail.product.is_custom;
              // PGSS-5663: inside Create Supplier Order (itself a product dialog) open the second slot, so
              // View Product stacks on top and closing it returns to the order with its choices intact.
              const openInspect = wrapsProductName(type) ? openViewProductDialog : openProductDialog;
              dispatch(
                openInspect({
                  data: productInitValues({
                    ...orderDetail.product,
                    discount: orderDetail.discount,
                    order_quantity: orderDetail.quantity,
                    customer_provider_number: orderDetail.customer_provider_number,
                    comments: orderDetail.comments,
                    decedent: orderDetail.decedent,
                    weight: orderDetail.weight,
                  }),
                  param: {
                    name: "edit_custom_product",
                    title: isCustom ? "View Custom Product" : "View Product",
                    orderType: param,
                    stage: orderType,
                    isReadOnly: true,
                    hideDialogActions: true,
                    isCustomProduct: isCustom,
                    isInventoryProduct: !isCustom,
                    hideAdditionalFields,
                    formOrderType: formOrderData?.order_type,
                    formOrderData: cloneDeep(formOrderData),
                  },
                })
              );
            };
            return (
              <Fragment key={fieldRowKey(data, index)}>
                <tr style={isInStockLine ? { opacity: 0.55, backgroundColor: '#f5f5f5', pointerEvents: 'none' } : undefined}>
                  <td className="w-40 px-14 py-8 px-8 py-4">{index + 1}</td>
                  <td className="w-64 px-8 py-4 product-name">
                  <Box display="flex" alignItems="center" width="100%">
                    {data?.comments && (
                      <IconButton color="inherit" className="comment-icon"
                        onClick={openProductInspectDialog}
                        invisible={!data?.comments}
                        style={{ backgroundColor: 'red',borderRadius: '50%', padding: '3px', marginRight:'8px'}}
                      >
                        <Tooltip title="View Comments">
                            <Icon sx={{ fontSize: '16px', color: 'white' }}>visibility</Icon>
                        </Tooltip>
                      </IconButton>
                      )}
                      <Tooltip title={orderDetail?.product?.name || ''}>
                      <Typography
                        // For direct shipment the product id will be in product_id
                        // to={`/products/${orderDetail.product.product_id || orderDetail.product.id}`}
                        // PGSS-5659: in Create Supplier Order the name wraps (full name on hover too).
                        // PGSS-5663: it still opens View Product, stacked on top of the dialog.
                        onClick={openProductInspectDialog}
                        className={wrapsProductName(type) ? "cursor-pointer" : "truncate cursor-pointer"}
                        style={productNameStyle(type, amber[900])}
                      >
                        {orderDetail?.product?.name}
                      </Typography>
                      </Tooltip>
                      {isInStockLine && (
                        <Tooltip title={inStockNote}>
                          <span style={{ marginLeft: 6, verticalAlign: 'middle', cursor: 'help', display: 'inline-flex', pointerEvents: 'auto' }}>
                            <FuseSvgIcon color="action" size={18}>
                              material-outline:inventory_2
                            </FuseSvgIcon>
                          </span>
                        </Tooltip>
                      )}
                      {/* </div> */}
                     </Box>
                  </td>
                  {type !== "order" && (
                    <>
                      <td className="px-8 py-4">
                        <span className="truncate">
                          {type === "supplierOrder"
                            ? orderDetail?.product?.sub_type_name
                            : orderDetail?.product?.subType
                          }
                        </span>
                      </td>
                    </>
                  )}
                  {type !== "order" && (
                    <td className="w-96 px-8 py-4">
                      <span className="truncate">
                        {orderDetail?.product?.weight
                          ? `${formatWeight(orderDetail?.product?.weight)}`
                          : ""}
                      </span>
                    </td>
                  )}
                  {type === "order"
                    ? orderCols(orderDetail, index)
                    : reOrderCols(orderDetail, index)}
                </tr>
              </Fragment>
            );
          })}
        </tbody>
        </>
      )}
      </table>
      {!hideVASPriceDetails && showVasPriceDetails(orderType, param, getValues().vas) && (
          <VasPriceList
            type={getVASPriceDetailsViewingType()}
            from="order"
            resetData={false}
            vasPriceDetailsData={orderData.vas_price_details}
            totalVASAmount={totalVASAmount}
            setTotalVASAmount={setTotalVASAmount}
          />
        )}
      {!loading && !hideTotalSection && (
      <div className="flex flex-1 grow-0  justify-between">
        <div className="w-1/2">
          {/* {props.displayOptions?.extra?.component && <ExtraComponent {...control} {...(props.displayOptions.extra.data || {})} />} */}
        </div>
        <div className="flex-end flex-col w-1/3">
          <Table className="simple mt-20">
            {type === "order" ? orderPricingNewCols : reOrderPricingNewCols}
          </Table>
        </div>
      </div>
      )}
      <ApprovalContent
        {...approvalDialog}
        onClose={() => setApprovalDialog({ open: false })}
        heading="Add Documents"
      />
      <DeleteConfirmation
        open={deleteConfirmationDialog.open}
        onClose={() => handleDeleteDialog(false)}
        onSubmit={handleDeleteSubmit}
      />
      </div>
    </div>
  );
}

export default OrderItems;


